import React, { useEffect, useState } from 'react';
import { api } from './auth';

const API = process.env.REACT_APP_API_URL || '';

export default function AccountSecurity({ user }) {
  const [status, setStatus] = useState({ enabled: false, enrolled_at: null, backup_codes_remaining: 0 });
  const [step, setStep] = useState('idle'); // idle | enrol_show_qr | enrol_confirm | enrol_codes | disable | regen_codes
  const [enrolData, setEnrolData] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = () => api.get('/mfa/status').then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const startEnrol = async () => {
    setError(null); setLoading(true);
    try {
      const r = await api.post('/mfa/enrol/start');
      setEnrolData(r.data);
      // Fetch the QR PNG through the authenticated client so the Bearer token is
      // attached, then turn it into a blob URL the <img> can show. A plain
      // <img src> cannot send the Authorization header, which is why the raw
      // endpoint returns 401 and the image appears broken.
      try {
        const qr = await api.get('/mfa/enrol/qr', { responseType: 'blob' });
        setQrUrl(URL.createObjectURL(qr.data));
      } catch (qrErr) {
        setQrUrl(null); // manual-entry key still works as a fallback
      }
      setStep('enrol_show_qr');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const confirmEnrol = async (e) => {
    e?.preventDefault();
    setError(null); setLoading(true);
    try {
      const r = await api.post('/mfa/enrol/confirm', { code, password });
      setBackupCodes(r.data.backup_codes);
      setStep('enrol_codes');
      setCode(''); setPassword('');
      if (qrUrl) { URL.revokeObjectURL(qrUrl); setQrUrl(null); }
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const disable = async (e) => {
    e?.preventDefault();
    setError(null); setLoading(true);
    try {
      await api.post('/mfa/disable', { password, code, is_backup_code: false });
      setStep('idle'); setCode(''); setPassword('');
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const regenerate = async (e) => {
    e?.preventDefault();
    setError(null); setLoading(true);
    try {
      const r = await api.post('/mfa/backup-codes/regenerate', { password, code, is_backup_code: false });
      setBackupCodes(r.data.backup_codes);
      setStep('enrol_codes');  // reuse same UI
      setCode(''); setPassword('');
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const cancel = () => {
    if (qrUrl) { URL.revokeObjectURL(qrUrl); setQrUrl(null); }
    setStep('idle'); setCode(''); setPassword(''); setError(null);
  };
  const finishCodes = () => { setBackupCodes(null); setStep('idle'); };
  const copy = (text) => navigator.clipboard?.writeText(text);

  return (
    <div style={card}>
      <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0, marginBottom: 6 }}>Account security</h2>
      <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 18 }}>
        Manage two-factor authentication for your account ({user?.email}).
      </p>

      {/* MFA STATUS BANNER */}
      <div style={{
        background: status.enabled ? '#F0FDF4' : '#FFFBEB',
        border: `1px solid ${status.enabled ? '#BBF7D0' : '#FDE68A'}`,
        borderRadius: 10, padding: '14px 16px', marginBottom: 18,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 18 }}>{status.enabled ? '✓' : '⚠'}</span>
          <strong style={{ fontSize: 14, color: status.enabled ? '#15803D' : '#92400E' }}>
            Two-factor authentication is {status.enabled ? 'enabled' : 'disabled'}
          </strong>
        </div>
        {status.enabled
          ? <p style={{ fontSize: 12, color: '#15803D', margin: 0 }}>
              Enrolled on {new Date(status.enrolled_at).toLocaleString()} · {status.backup_codes_remaining} backup codes remaining
            </p>
          : <p style={{ fontSize: 12, color: '#92400E', margin: 0 }}>
              Strongly recommended for super-admin and company-admin accounts handling personal data.
            </p>}
      </div>

      {/* IDLE: show appropriate primary action */}
      {step === 'idle' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!status.enabled && <button onClick={startEnrol} disabled={loading} style={btnPrimary}>{loading ? 'Please wait…' : 'Enable two-factor authentication'}</button>}
          {status.enabled && <>
            <button onClick={() => setStep('regen_codes')} style={btnSecondary}>Regenerate backup codes</button>
            <button onClick={() => setStep('disable')} style={{ ...btnSecondary, color: '#DC2626', borderColor: '#FECACA' }}>Disable MFA</button>
          </>}
        </div>
      )}

      {error && step === 'idle' && <div style={errBox}>{error}</div>}

      {/* ENROL: SHOW QR */}
      {step === 'enrol_show_qr' && enrolData && (
        <div>
          <h3 style={h3}>1. Scan this QR code in your authenticator app</h3>
          <p style={pHint}>Use Google Authenticator, Authy, 1Password, Microsoft Authenticator, or similar.</p>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 16 }}>
            {qrUrl
              ? <img src={qrUrl}
                  alt="QR code"
                  style={{ width: 200, height: 200, border: '1px solid #E5E7EB', borderRadius: 8, padding: 8, background: '#fff' }} />
              : <div style={{ width: 200, height: 200, border: '1px solid #E5E7EB', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 12, color: '#6B7280', padding: 8, background: '#fff', boxSizing: 'border-box' }}>
                  QR image unavailable. Use the manual key on the right.
                </div>}
            <div style={{ flex: 1, minWidth: 200 }}>
              <p style={{ fontSize: 11, color: '#6B7280', marginTop: 0, marginBottom: 4 }}>Or enter this code manually:</p>
              <pre style={{ background: '#F8F9FB', border: '1px solid #E5E7EB', borderRadius: 6, padding: '8px 10px',
                fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all', whiteSpace: 'pre-wrap', margin: 0 }}>
                {enrolData.secret}
              </pre>
              <button onClick={() => copy(enrolData.secret)} style={{ ...btnSecondary, marginTop: 6, fontSize: 11, padding: '4px 10px' }}>Copy code</button>
            </div>
          </div>

          <h3 style={h3}>2. Confirm with a code from your authenticator</h3>
          <form onSubmit={confirmEnrol}>
            <div style={{ display: 'grid', gap: 10 }}>
              <input type="password" placeholder="Your password" value={password} onChange={e => setPassword(e.target.value)} required style={inp} autoComplete="current-password" />
              <input type="text" inputMode="numeric" placeholder="6-digit code" value={code} onChange={e => setCode(e.target.value)} required maxLength={6}
                style={{ ...inp, fontFamily: 'monospace', fontSize: 18, letterSpacing: 4, textAlign: 'center' }} autoComplete="one-time-code" />
            </div>
            {error && <div style={errBox}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="submit" disabled={loading || !code || !password} style={btnPrimary}>
                {loading ? 'Verifying…' : 'Confirm and enable'}
              </button>
              <button type="button" onClick={cancel} style={btnSecondary}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* SHOW BACKUP CODES (after enrol or regen) */}
      {step === 'enrol_codes' && backupCodes && (
        <div>
          <h3 style={h3}>Save your backup codes</h3>
          <p style={pHint}>
            Each code can be used <strong>once</strong> to sign in if you lose access to your authenticator.
            <strong style={{ color: '#DC2626' }}> Save them now, they will not be shown again.</strong>
          </p>
          <div style={{ background: '#0F172A', borderRadius: 8, padding: '14px 18px', marginBottom: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(120px,1fr))', gap: 10 }}>
              {backupCodes.map((c, i) => (
                <code key={i} style={{ color: '#F8FAFC', fontFamily: 'monospace', fontSize: 14, textAlign: 'center', padding: '6px 0' }}>{c}</code>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => copy(backupCodes.join('\n'))} style={btnSecondary}>Copy all to clipboard</button>
            <button onClick={() => {
              const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'eyez-mfa-backup-codes.txt';
              a.click(); URL.revokeObjectURL(url);
            }} style={btnSecondary}>Download as .txt</button>
            <button onClick={finishCodes} style={btnPrimary}>I've saved them</button>
          </div>
        </div>
      )}

      {/* DISABLE MFA */}
      {step === 'disable' && (
        <form onSubmit={disable}>
          <h3 style={h3}>Disable two-factor authentication</h3>
          <p style={pHint}>This removes 2FA from your account. Confirm with your password and a current code.</p>
          <div style={{ display: 'grid', gap: 10 }}>
            <input type="password" placeholder="Your password" value={password} onChange={e => setPassword(e.target.value)} required style={inp} />
            <input type="text" inputMode="numeric" placeholder="6-digit code" value={code} onChange={e => setCode(e.target.value)} required maxLength={6}
              style={{ ...inp, fontFamily: 'monospace', fontSize: 18, letterSpacing: 4, textAlign: 'center' }} />
          </div>
          {error && <div style={errBox}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" disabled={loading || !code || !password} style={{ ...btnPrimary, background: '#DC2626' }}>
              {loading ? 'Disabling…' : 'Confirm disable MFA'}
            </button>
            <button type="button" onClick={cancel} style={btnSecondary}>Cancel</button>
          </div>
        </form>
      )}

      {/* REGEN BACKUP CODES */}
      {step === 'regen_codes' && (
        <form onSubmit={regenerate}>
          <h3 style={h3}>Regenerate backup codes</h3>
          <p style={pHint}>Existing backup codes are <strong>invalidated immediately</strong> when you generate new ones. Confirm with your password and a current authenticator code.</p>
          <div style={{ display: 'grid', gap: 10 }}>
            <input type="password" placeholder="Your password" value={password} onChange={e => setPassword(e.target.value)} required style={inp} />
            <input type="text" inputMode="numeric" placeholder="6-digit code" value={code} onChange={e => setCode(e.target.value)} required maxLength={6}
              style={{ ...inp, fontFamily: 'monospace', fontSize: 18, letterSpacing: 4, textAlign: 'center' }} />
          </div>
          {error && <div style={errBox}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" disabled={loading || !code || !password} style={btnPrimary}>
              {loading ? 'Generating…' : 'Generate new codes'}
            </button>
            <button type="button" onClick={cancel} style={btnSecondary}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

const card = { background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' };
const inp = { padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const btnPrimary = { background: '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 500, fontSize: 13, cursor: 'pointer' };
const btnSecondary = { background: '#fff', color: '#111827', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 14px', fontWeight: 500, fontSize: 13, cursor: 'pointer' };
const h3 = { fontSize: 13, fontWeight: 600, color: '#111827', margin: '0 0 6px 0' };
const pHint = { fontSize: 12, color: '#6B7280', marginTop: 0, marginBottom: 12, lineHeight: 1.5 };
const errBox = { background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', color: '#DC2626', fontSize: 13, marginTop: 10 };
